import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography, Button } from '@mui/material';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { ConnectionStatus as CS } from '../../services/websocketManager';

export const ConnectionStatus: React.FC = () => {
  const { t } = useTranslation();
  const { status, reconnectAttempts, opportunityCount, reconnect, isConnected } = useConnectionStatus();
  
  const getStatusColor = () => {
    switch (status) {
      case CS.CONNECTED:
        return '#00ff88';
      case CS.CONNECTING:
      case CS.RECONNECTING:
        return '#ff9500';
      case CS.DISCONNECTED:
        return '#ff0066';
      default:
        return '#999';
    }
  };
  
  const getStatusIcon = () => {
    switch (status) {
      case CS.CONNECTED:
        return '🟢';
      case CS.CONNECTING:
      case CS.RECONNECTING:
        return '🟡';
      case CS.DISCONNECTED:
        return '🔴';
      default:
        return '⚪';
    }
  };
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: 16,  // Changed to left to avoid conflict with language switcher
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Chip
        icon={<Typography component="span">{getStatusIcon()}</Typography>}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: getStatusColor(),
              }}
            >
              {t(`connection.${status.toLowerCase()}`)}
            </Typography>
            {status === CS.RECONNECTING && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                (Attempt {reconnectAttempts})
              </Typography>
            )}
          </Box>
        }
        sx={{
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${getStatusColor()}`,
          '& .MuiChip-icon': {
            marginRight: 1,
          },
        }}
      />
      {!isConnected && (
        <Button
          size="small"
          variant="contained"
          onClick={reconnect}
          sx={{
            background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
              opacity: 0.9,
            },
          }}
        >
          {t('common.retry')}
        </Button>
      )}
    </Box>
  );
};